<template>
	<div
		v-if="mergedRecordsList && mergedRecordsList.length > 0"
		class="merged-records-container mb-6"
	>
		<v-card
			v-for="(mergedRecord, idx) in mergedRecordsList"
			:key="`merge-${idx}`"
			class="mb-3"
			outlined
		>
			<v-card-title
				class="merged-record-title"
				@click="toggleExpanded(idx)"
			>
				<v-icon
					class="mr-2"
					color="orange"
				>
					mdi-merge
				</v-icon>
				Merged Record Information
				<span
					v-if="mergedRecordsList.length > 1"
					class="text-caption ml-2"
				>
					({{ idx + 1 }} of {{ mergedRecordsList.length }})
				</span>
				<v-spacer />
				<v-icon>
					{{ expandedStates[idx] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
				</v-icon>
			</v-card-title>
			<v-expand-transition>
				<v-card-text v-show="expandedStates[idx]">
					<div class="text-body-2 mb-3">
						<strong>
							Merged SyrenisID:
						</strong> {{ mergedRecord.mergedSyrenisId }}
					</div>
					<div class="text-body-2 mb-3">
						<strong>
							Merge Date:
						</strong> {{ formatDateTime(mergedRecord.mergeDate) }}
					</div>
					<div
						v-if="mergedRecord.preferences && mergedRecord.preferences.length > 0"
						class="mb-3"
					>
						<div class="text-subtitle-2 font-weight-bold mb-2">
							Merged Preferences ({{ mergedRecord.preferences.length }})
						</div>
						<v-simple-table dense>
							<template #default>
								<thead>
									<tr>
										<th>
											Channel
										</th>
										<th>
											Brand
										</th>
										<th>
											Value
										</th>
										<th>
											Date Submitted
										</th>
									</tr>
								</thead>
								<tbody>
									<tr
										v-for="(pref, index) in mergedRecord.preferences"
										:key="index"
									>
										<td>
											{{ pref.channelName }}
										</td>
										<td>
											{{ pref.brandName }}
										</td>
										<td>
											<v-chip
												:color="pref.preferenceValue === 'Opt-In' ? 'success' : 'error'"
												x-small
												label
											>
												{{ pref.preferenceValue }}
											</v-chip>
										</td>
										<td>
											{{ pref.dateSubmitted }}
										</td>
									</tr>
								</tbody>
							</template>
						</v-simple-table>
					</div>
					<div v-if="mergedRecord.contactDetails && mergedRecord.contactDetails.length > 0">
						<div class="text-subtitle-2 font-weight-bold mb-2">
							Contact Details
						</div>
						<v-simple-table dense>
							<template #default>
								<thead>
									<tr>
										<th>
											Field
										</th>
										<th>
											Value
										</th>
									</tr>
								</thead>
								<tbody>
									<tr
										v-for="(detail, index) in mergedRecord.contactDetails"
										:key="index"
									>
										<td>
											{{ detail.label }}
										</td>
										<td>
											{{ detail.value }}
										</td>
									</tr>
								</tbody>
							</template>
						</v-simple-table>
					</div>
				</v-card-text>
			</v-expand-transition>
		</v-card>
	</div>
</template>

<script>
import { contactDetailsFields } from '../../../../../../../shared/state/configuration.js'

export default {
	props: {
		mergedRecords: {
			type: Array,
			default: () => []
		},
		selectedDate: {
			type: Date,
			default: null
		}
	},
	setup () { return { contactDetailsFields } },
	data () {
		return {
			expandedStates: {}
		}
	},
	computed: {
		excludedStandDakFields () {
			return ['Submission', 'Submissions', 'DateUpdatedUTC', 'DateUpdated', 'DateAdded', 'DateAddedUTC', 'DateAddedUtc', 'SyrenisID', 'CRC', 'GUID']
		},
		mergedRecordsList () {
			if (!this.mergedRecords || this.mergedRecords.length === 0 || !this.selectedDate) {
				return []
			}

			const selectedDate = new Date(this.selectedDate)
			selectedDate.setHours(0, 0, 0, 0)
			const selectedDateStart = selectedDate.getTime()

			// Find all merges that occurred on the selected date
			const applicableMerges = this.mergedRecords.filter(mr => {
				const mergeDate = new Date(mr.mergeDate)
				mergeDate.setHours(0, 0, 0, 0)
				const mergeDateTime = mergeDate.getTime()
				return mergeDateTime === selectedDateStart
			})

			if (applicableMerges.length === 0) {
				return []
			}

			// Sort merges by time (if available) or keep original order
			const sortedMerges = applicableMerges.sort((a, b) => {
				const aTime = new Date(a.mergeDate).getTime()
				const bTime = new Date(b.mergeDate).getTime()
				return aTime - bTime
			})

			return sortedMerges.map(applicableMerge => this.buildMergedRecordInfo(applicableMerge))
		}
	},
	watch: {
		mergedRecordsList: {
			handler (newList) {
				// Initialize expanded states for new items
				if (newList && newList.length > 0) {
					newList.forEach((_, idx) => {
						if (!(idx in this.expandedStates)) {
							this.$set(this.expandedStates, idx, false)
						}
					})
				}
			},
			immediate: true
		}
	},
	methods: {
		buildMergedRecordInfo (applicableMerge) {
			const contactDetailsMap = new Map()

			// First, get contact details from merged standDaks
			if (applicableMerge.mergedStandDaks && applicableMerge.mergedStandDaks.length > 0) {
				const latestStandDak = applicableMerge.mergedStandDaks[applicableMerge.mergedStandDaks.length - 1]
				this.extractContactDetails(latestStandDak, contactDetailsMap)
			}

			// Convert map to array
			const contactDetails = Array.from(contactDetailsMap.values())

			return {
				mergedSyrenisId: applicableMerge.mergedSyrenisId,
				mergeDate: applicableMerge.mergeDate,
				preferences: applicableMerge.mergedPreferences || [],
				contactDetails
			}
		},
		extractContactDetails (standDak, contactDetailsMap) {
			Object.keys(standDak).forEach(key => {
				if (!this.excludedStandDakFields.includes(key) && standDak[key] && standDak[key] !== '') {
					contactDetailsMap.set(key, {
						label: (this.contactDetailsFields.find(f => f.fieldName.toLowerCase() === key.toLowerCase()) || {}).fieldLabel || key.replace(/([A-Z])/g, ' $1').trim(),
						value: standDak[key]
					})
				}
			})
		},
		formatDate (date) {
			if (!date) return ''
			const d = new Date(date)
			if (isNaN(d.getTime())) return 'Invalid Date'
			return `${d.getDate()} ${d.toLocaleString('en-GB', { month: 'short' })} ${d.getFullYear()}`
		},
		formatDateTime (date) {
			if (!date) return ''
			const d = new Date(date)
			if (isNaN(d.getTime())) return 'Invalid Date'
			const datePart = `${d.getDate()} ${d.toLocaleString('en-GB', { month: 'short' })} ${d.getFullYear()}`
			const timePart = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
			return `${datePart} ${timePart}`
		},
		toggleExpanded (idx) {
			this.$set(this.expandedStates, idx, !this.expandedStates[idx])
		}
	}
}
</script>

<style scoped>
.merged-record-title {
	cursor: pointer;
	user-select: none;
	font-size: 1.1rem !important;
	padding: 12px 16px !important;
}
.merged-record-title:hover {
	background-color: rgba(255, 152, 0, 0.05);
}
</style>
